/**
 * FACEIT FORECAST — match win-probability overlay for FACEIT
 *
 * Declares a single web-accessible-resource group scoped to `https://www.faceit.com`
 * only, not to every page. The group holds stable, vendor-named config JSON
 * (`src/config/selectors.json`, `endpoints.json`, ...) and icon assets
 * (`src/visual/icons/logo128.png`, `logo.svg`), none using `use_dynamic_url`.
 *
 * Because the resources are origin-scoped, they are genuinely unreachable from anywhere
 * but faceit.com — a negative from any other page says nothing about the browser, only
 * about where the probe is running. Hence `requiredOrigin`.
 *
 * Manifest verified against version 1.8.2.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'mpkkcddegpblmobincjkbpgfcbejjbcp';

export const faceitForecast: ExtensionDefinition = {
  id: ID,
  name: 'FACEIT FORECAST',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  requiredOrigin: 'https://www.faceit.com',
  notes:
    'WAR scoped to www.faceit.com, so only that site can detect it. Several stable ' +
    'vendor-named config JSON and icon paths make detection conclusive there.',
  signals: [
    {
      id: 'war-selectors-json',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/config/selectors.json — a distinctive vendor config file, exposed ' +
        'only to faceit.com origins.',
      weight: 1,
      run: () => probeFetch(ID, 'src/config/selectors.json'),
    },
    {
      id: 'war-logo-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load src/visual/icons/logo128.png via <img> — a vendor-named icon ' +
        'corroborating the match without a CORS grant.',
      weight: 0.5,
      run: () => probeImage(ID, 'src/visual/icons/logo128.png'),
    },
    {
      id: 'war-metrics-json',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/config/metrics.json — a second config path independent of the ' +
        'selectors file and the icon.',
      weight: 0.5,
      run: () => probeFetch(ID, 'src/config/metrics.json'),
    },
  ],
};
